import React from 'react';
import { 
  Popover, 
  Box, 
  Typography, 
  FormGroup, 
  FormControlLabel, 
  Checkbox, 
  Divider, 
  Button,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

const ColumnFilterPopover = ({ 
  anchorEl, 
  onClose, 
  allColumns, 
  hiddenColumns, 
  onToggle, 
  onReset,
  onReorder
}) => {
  const open = Boolean(anchorEl);
  const id = open ? 'column-filter-popover' : undefined;

  const onDragEnd = (result) => {
    if (!result.destination) return;
    onReorder(result.source.index, result.destination.index);
  };

  return (
    <Popover
      id={id}
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      PaperProps={{
        sx: {
          width: 280,
          maxHeight: 500,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
        }
      }}
    >
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Manage Columns
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      
      <Divider />
      
      <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="column-list">
            {(provided) => (
              <Box 
                {...provided.droppableProps} 
                ref={provided.innerRef}
                sx={{ display: 'flex', flexDirection: 'column' }}
              >
                {allColumns.map((column, index) => {
                  const isMandatory = column.field === 'symbol';
                  const isChecked = !hiddenColumns.includes(column.field);
                  
                  return (
                    <Draggable 
                      key={column.field} 
                      draggableId={column.field} 
                      index={index}
                      isDragDisabled={isMandatory}
                    >
                      {(draggableProvided, snapshot) => (
                        <Box
                          ref={draggableProvided.innerRef}
                          {...draggableProvided.draggableProps}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            backgroundColor: snapshot.isDragging ? 'action.selected' : 'transparent',
                            '&:hover': {
                              backgroundColor: 'action.hover',
                            },
                            mb: 0.5,
                            ...draggableProvided.draggableProps.style
                          }}
                        >
                          {/* Drag Handle */}
                          <Box 
                            {...draggableProvided.dragHandleProps}
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              mr: 1, 
                              color: isMandatory ? 'text.disabled' : 'text.secondary',
                              cursor: isMandatory ? 'default' : 'grab'
                            }}
                          >
                            <DragIndicatorIcon sx={{ fontSize: '1.2rem' }} />
                          </Box>

                          <FormControlLabel
                            control={
                              <Checkbox 
                                size="small" 
                                checked={isChecked}
                                onChange={() => onToggle(column.field)}
                                disabled={isMandatory}
                                sx={{ p: 0.5 }}
                              />
                            }
                            label={
                              <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: isChecked ? 500 : 400 }}>
                                {column.headerName}
                              </Typography>
                            }
                            sx={{ m: 0, flex: 1 }}
                          />
                        </Box>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      </Box>
      
      <Divider />
      
      <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'center' }}>
        <Button 
          fullWidth
          size="small" 
          startIcon={<RestartAltIcon />}
          onClick={onReset}
          sx={{ textTransform: 'none' }}
        >
          Reset to Default
        </Button>
      </Box>
    </Popover>
  );
};

export default ColumnFilterPopover;
